import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    TextField,
    Tooltip,
    IconButton,
    Autocomplete,
    Button,
    Popper,
    Box,
    Typography,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveIcon from '@mui/icons-material/Remove';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/system';
import AssetService from '../../../Services/assets.service';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import {
    stripRelatesToFromDescription,
    appendRelatesToDescription,
    stripSoldOn,
} from '../../../Utils/assetDescriptionUtils';
import { parseApiDateToUtcIso } from '../../../Utils/dateParseUtils';
import SaleTransactionPicker from './SaleTransactionPicker';

const StyledContainer = styled('div')({
    position: 'absolute',
    top: '3%',
    left: '30%',
    transform: 'translate(-50%, 0)',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '0',
    minHeight: '50vh',
    maxHeight: '88vh',
    overflow: 'hidden',
    width: '720px',
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(48, 79, 255, 0.18), 0 2px 8px rgba(0,0,0,0.10)',
    border: '1px solid #c5ceff',
});

const StyledListItem = styled('li')({
    '&:hover': {
        backgroundColor: '#F5F5F6',
        cursor: 'pointer',
    },
});

const StyledPopperOuter = styled(Popper)({
    zIndex: '99999 !important',
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.32)',
    },
    '& .MuiAutocomplete-listbox': {
        padding: 8,
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        '&[aria-selected="true"], &.Mui-focused, &:hover': {
            background: '#F9AA2A !important',
        },
    },
});

const SectionTitle = ({ icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon && <Box sx={{ color: '#304FFF', display: 'flex' }}>{icon}</Box>}
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1a237e' }}>
            {children}
        </Typography>
    </Box>
);

const FieldLabel = ({ required, children }) => (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#555', mb: 0.5, textAlign: 'right', width: '100%' }}>
        {required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
        {children}
    </Typography>
);

const sameAssetId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
};

const normalizeRelation = (relation) => {
    if (!relation) return null;
    return {
        ...relation,
        rootAssetId: relation.rootAssetId ?? relation.RootAssetId,
        relatedAssetIds: relation.relatedAssetIds ?? relation.RelatedAssetIds,
        relatedAssets: relation.relatedAssets ?? relation.RelatedAssets,
        rootAsset: relation.rootAsset ?? relation.RootAsset,
    };
};

const getRelationChildIds = (relation) => {
    if (!relation) return [];
    const rel = normalizeRelation(relation);
    const rootId = rel.rootAssetId || rel.rootAsset?.id;
    const relatedIds = rel.relatedAssetIds;
    const relatedAssets = rel.relatedAssets;
    if (Array.isArray(relatedIds) && relatedIds.length > 0) {
        return relatedIds.filter((id) => id && !sameAssetId(id, rootId));
    }
    return (relatedAssets || [])
        .map((ra) => ra?.id ?? ra?.Id)
        .filter((id) => id && !sameAssetId(id, rootId));
};

const mapRelationToRelatedAssets = (relation, allAssets = []) => {
    if (!relation) return [];
    const rel = normalizeRelation(relation);
    const childIds = getRelationChildIds(rel);
    const relatedAssets = rel.relatedAssets || [];
    const seen = new Set();
    const mapped = [];

    const pushMapped = (item) => {
        const id = item?.id ?? item?.Id;
        if (!id || seen.has(String(id).toLowerCase())) return;
        seen.add(String(id).toLowerCase());
        const full = allAssets.find((a) => sameAssetId(a.id, id));
        mapped.push(full || (item?.id ? item : { ...item, id }));
    };

    childIds.forEach((id) => {
        const full = allAssets.find((a) => sameAssetId(a.id, id));
        const summary = relatedAssets.find((ra) => sameAssetId(ra?.id ?? ra?.Id, id));
        pushMapped(full || summary || { id });
    });

    if (mapped.length === 0 && Array.isArray(relatedAssets)) {
        relatedAssets.forEach((ra) => {
            const id = ra?.id ?? ra?.Id;
            if (!id || sameAssetId(id, rel.rootAssetId)) return;
            pushMapped(allAssets.find((a) => sameAssetId(a.id, id)) || ra);
        });
    }

    return mapped;
};

const findRootRelationInList = (relations, rootAssetId) => {
    if (!rootAssetId || !Array.isArray(relations)) return null;
    const found = relations.find((r) =>
        sameAssetId(normalizeRelation(r)?.rootAssetId, rootAssetId)
    );
    return found ? normalizeRelation(found) : null;
};

const findParentRelationInList = (relations, assetId) => {
    if (!assetId || !Array.isArray(relations)) return null;
    const found =
        relations.find((r) => {
            const rel = normalizeRelation(r);
            const related = rel.relatedAssets || [];
            return related.some((ra) => sameAssetId(ra?.id ?? ra?.Id, assetId));
        }) ||
        relations.find((r) => {
            const rel = normalizeRelation(r);
            const rootId = rel.rootAssetId || rel.rootAsset?.id;
            const childIds = getRelationChildIds(rel);
            return childIds.some((id) => sameAssetId(id, assetId)) && !sameAssetId(rootId, assetId);
        });
    return found ? normalizeRelation(found) : null;
};

const relationHasLinkedChildren = (relation) => getRelationChildIds(relation).length > 0;

const EditAsset = ({
    caseId,
    token,
    asset,
    assetGroups,
    assets = [],
    assetRelations = [],
    refreshRelations,
    open,
    onClose,
    onSave,
    onDelete,
    viewDocument,
    isDraggable = true,
    accountantId,
    sortCodeIdOptions = [],
}) => {

    const { i18n, t } = useTranslation();
    const direction =i18n.dir();
    const nodeRef = useRef(null);
    const [editedAsset, setEditedAsset] = useState({ ...asset });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [saleTransactionId, setSaleTransactionId] = useState('');
    const [saleTransactionType, setSaleTransactionType] = useState('Incomes');
    const [isLinking, setIsLinking] = useState(false);
    const [showSalePicker, setShowSalePicker] = useState(false);
    const [selectedRelatedAssets, setSelectedRelatedAssets] = useState([]);
    const [selectedAssetsDialogOpen, setSelectedAssetsDialogOpen] = useState(false);
    const [resolvedRelation, setResolvedRelation] = useState(null);

    useEffect(() => {
        if (asset) setEditedAsset({ ...asset });
    }, [asset]);

    const currentRelation = useMemo(() => {
        if (resolvedRelation) return resolvedRelation;
        return findRootRelationInList(assetRelations, asset?.id);
    }, [asset?.id, assetRelations, resolvedRelation]);

    const parentRelation = useMemo(() => {
        return findParentRelationInList(assetRelations, asset?.id);
    }, [asset, assetRelations]);

    const isChildAsset = Boolean(parentRelation);

    const rootAssetForSale = useMemo(() => {
        if (!isChildAsset || !parentRelation) return null;
        const rootId = parentRelation.rootAssetId || parentRelation.rootAsset?.id;
        if (!rootId) return null;
        return (assets || []).find((a) => sameAssetId(a.id, rootId)) || parentRelation.rootAsset || null;
    }, [isChildAsset, parentRelation, assets]);

    const isGroupSoldViaRoot = useMemo(() => {
        if (!rootAssetForSale) return false;
        return !!(
            rootAssetForSale.sellDate ||
            rootAssetForSale.saleIncomeId ||
            rootAssetForSale.saleJournalEntryId
        );
    }, [rootAssetForSale]);

    useEffect(() => {
        if (!open) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        if (!asset?.id || !caseId || !token) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        if (isChildAsset) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        let cancelled = false;

        const applyRelation = (relation) => {
            if (cancelled) return;
            setResolvedRelation(relation || null);
            setSelectedRelatedAssets(mapRelationToRelatedAssets(relation, assets));
        };

        const relationFromProps = findRootRelationInList(assetRelations, asset.id);
        if (relationFromProps && relationHasLinkedChildren(relationFromProps)) {
            applyRelation(relationFromProps);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const tryApplyFromCaseRelations = async () => {
                const allResp = await AssetService.GetAssetRelationsByCaseId(caseId, token);
                if (cancelled) return false;
                if (allResp?.success && Array.isArray(allResp.data)) {
                    const found = findRootRelationInList(allResp.data, asset.id);
                    if (found && relationHasLinkedChildren(found)) {
                        applyRelation(found);
                        return true;
                    }
                }
                return false;
            };

            try {
                const resp = await AssetService.GetAssetRelationByRootAsset(caseId, asset.id, token);
                if (cancelled) return;
                if (resp?.success && resp.data) {
                    applyRelation(normalizeRelation(resp.data));
                    return;
                }
            } catch (err) {
                if (cancelled) return;
                try {
                    if (await tryApplyFromCaseRelations()) return;
                } catch (_) {
                    /* fall through */
                }
            }

            try {
                if (await tryApplyFromCaseRelations()) return;
            } catch (_) {
                /* fall through */
            }

            if (relationFromProps) {
                applyRelation(relationFromProps);
            } else {
                applyRelation(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, asset?.id, caseId, token, assetRelations, assets, isChildAsset]);

    useEffect(() => {
        if (!open || isChildAsset || !asset?.id) return;
        const relation = resolvedRelation || findRootRelationInList(assetRelations, asset.id);
        if (!relation || !relationHasLinkedChildren(relation)) return;

        const mapped = mapRelationToRelatedAssets(relation, assets);
        if (mapped.length === 0) return;

        setSelectedRelatedAssets((prev) => {
            const next = mapped.map((m) => assets.find((a) => sameAssetId(a.id, m.id)) || m);
            if (
                prev.length === next.length &&
                prev.every((p, i) => sameAssetId(p?.id, next[i]?.id))
            ) {
                return prev;
            }
            return next;
        });
    }, [open, asset?.id, assets, assetRelations, resolvedRelation, isChildAsset]);

    const eligibleRelatedAssets = useMemo(() => {
        if (!Array.isArray(assets) || !asset?.id) return [];

        const childIdsLinkedElsewhere = new Set();
        const rootIdsOfOtherRelations = new Set();
        (assetRelations || []).forEach((rel) => {
            if (rel?.rootAssetId && !sameAssetId(rel.rootAssetId, asset.id)) {
                rootIdsOfOtherRelations.add(rel.rootAssetId);
            }
            (rel?.relatedAssets || []).forEach((ra) => {
                const raId = ra?.id ?? ra?.Id;
                if (raId && !sameAssetId(rel?.rootAssetId, asset.id)) {
                    childIdsLinkedElsewhere.add(raId);
                }
            });
        });

        const filtered = assets.filter((a) => {
            if (!a || !a.id) return false;
            if (sameAssetId(a.id, asset.id)) return false;
            if ([...childIdsLinkedElsewhere].some((id) => sameAssetId(id, a.id))) return false;
            if ([...rootIdsOfOtherRelations].some((id) => sameAssetId(id, a.id))) return false;
            return true;
        });

        const sameGroupId = asset?.assetGroupId;
        return [...filtered].sort((a, b) => {
            const aSame = sameGroupId && a.assetGroupId === sameGroupId ? 0 : 1;
            const bSame = sameGroupId && b.assetGroupId === sameGroupId ? 0 : 1;
            if (aSame !== bSame) return aSame - bSame;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [assets, assetRelations, asset]);

    const validateFields = () => {
        const { name, originalPrice, depreciationPercentage, purchaseDate } = editedAsset;
        return (
            name?.trim() !== '' &&
            !isNaN(originalPrice) && originalPrice !== null && originalPrice !== undefined &&
            !isNaN(depreciationPercentage) && depreciationPercentage !== null && depreciationPercentage !== undefined &&
            purchaseDate !== null
        );
    };

    useEffect(() => {
        setIsSaveDisabled(!validateFields());
    }, [editedAsset]);

    const inputPropsStyle = {
        disableUnderline: true,
        sx: {
            height: '44px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1.5px solid transparent',
            fontSize: 13,
            '&:focus-within': {
                border: '2px solid #304FFF',
            },
        },
    };

    const PopperWithDirection = (props) => (
        <StyledPopperOuter {...props} style={{ ...props.style, direction }} placement="bottom-start" disablePortal={false} />
    );

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
            fontSize: 13,
        },
    };

    const applySellDateRemoval = (prev) => ({
        ...prev,
        sellDate: null,
        sellAmount: null,
        saleIncomeId: null,
        saleJournalEntryId: null,
        description: stripSoldOn(prev.description || ''),
    });

    const handleRemoveSellDate = () => {
        if (isChildAsset) return;
        setEditedAsset((prev) => applySellDateRemoval(prev));
    };

    const handleChange = (field, value) => {
        if (field === 'assetGroup') {
            setEditedAsset((prev) => ({
                ...prev,
                depreciationPercentage: value.depreciationPercentage,
                assetGroupId: value?.assetGroupId,
            }));
        } else if (field === 'assetNumber') {
            const matchedAsset = (assets || []).find(
                (a) => a.id !== editedAsset.id && String(a.assetNumber) === String(value)
            );
            const newDesc = matchedAsset
                ? appendRelatesToDescription(editedAsset.description || '', matchedAsset.name)
                : stripRelatesToFromDescription(editedAsset.description || '');
            setEditedAsset((prev) => ({ ...prev, assetNumber: value, description: newDesc }));
        } else if (field === 'sellDate' && !value) {
            if (isChildAsset) return;
            setEditedAsset((prev) => applySellDateRemoval({ ...prev, sellDate: null }));
        } else {
            setEditedAsset((prev) => ({ ...prev, [field]: value }));
        }
    };

    const buildAssetDeleteBlockedSummary = (checkData) => {
        const hasEnhancements = checkData.usedInCapitalGainEnhancements;
        const hasReplacement = checkData.usedInAssetReplacement;

        if (hasEnhancements && hasReplacement) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר ליתרת השבחות ולשחלוף נכסים.\nיש להסיר קודם את הקישורים מתוך טופס רווח הון.';
        }
        if (hasEnhancements) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר ליתרת השבחות ברווח הון.\nיש להסיר קודם את הקישור מתוך טופס רווח הון.';
        }
        if (hasReplacement) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר לשחלוף נכסים ברווח הון.\nיש להסיר קודם את הקישור מתוך טופס רווח הון.';
        }
        return 'הנכס מקושר לתהליך אחר';
    };

    const handleDeleteClick = async () => {
        try {
            const assetId = editedAsset?.id ?? editedAsset?.Id;
            if (!assetId || !caseId || !token) return;

            const checkResp = await AssetService.CanDeleteAsset(caseId, assetId, token);
            const checkData = checkResp?.data;

            if (checkData && checkData.canDelete === false) {
                const reasons = checkData.blockingReasons || [];
                const summary = buildAssetDeleteBlockedSummary(checkData);

                await Swal.fire({
                    icon: 'warning',
                    title: 'לא ניתן למחוק את הנכס',
                    html: `
                        <div style="text-align:right; direction:rtl;">
                            <div style="margin-bottom:12px;">${summary.replace(/\n/g, '<br/>')}</div>
                            ${reasons.length > 0 ? `
                                <ul style="text-align:right; padding-right:20px; margin:0;">
                                    ${reasons.map((r) => `<li>${r}</li>`).join('')}
                                </ul>
                            ` : ''}
                        </div>
                    `,
                    confirmButtonText: 'סגור',
                });
                return;
            }

            const assetLabel = [editedAsset.name, editedAsset.assetNumber]
                .filter(Boolean)
                .join(' - ');

            const result = await Swal.fire({
                title: t('confirmDeleteTitle'),
                html: assetLabel
                    ? `<p style="margin:0 0 8px">${t('AreYouSureDeleteAsset')}</p><p style="margin:0"><strong>${assetLabel}</strong></p>`
                    : `<p style="margin:0">${t('AreYouSureDeleteAsset')}</p>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d32f2f',
                cancelButtonColor: '#999',
                confirmButtonText: t('Delete'),
                cancelButtonText: t('Cancel'),
            });

            if (!result.isConfirmed) return;
            onDelete(editedAsset.id);
        } catch (error) {
            console.error('Failed to check asset delete permission:', error);
            toast.error('שגיאה בבדיקת אפשרות מחיקת הנכס');
        }
    };

    const handleSave = async () => {
        if (!validateFields()) {
            toast.error(t('Please fill all required fields'));
            return;
        }
        try {
            const cleanedDescription = isChildAsset
                ? stripSoldOn(editedAsset.description || '')
                : editedAsset.sellDate
                    ? (editedAsset.description || '')
                    : stripSoldOn(editedAsset.description || '');

            const updatedAsset = {
                Id: editedAsset.id,
                CaseId: caseId,
                AssetNumber: editedAsset.assetNumber,
                Name: editedAsset.name,
                Description: cleanedDescription,
                PurchaseDate: editedAsset.purchaseDate,
                ActivationDate: editedAsset.activationDate || editedAsset.purchaseDate,
                SellDate: editedAsset.sellDate || null,
                SellAmount: editedAsset.sellAmount
                    ? parseFloat(editedAsset.sellAmount)
                    : null,
                OriginalPrice: parseFloat(editedAsset.originalPrice),
                DepreciationPercentage: parseFloat(editedAsset.depreciationPercentage),
                AssetGroupId: editedAsset.assetGroupId || null,
                ExpenseId: editedAsset.expenseId || null,
                JournalEntryId: editedAsset.journalEntryId || null,
                SaleIncomeId: editedAsset.saleIncomeId || null,
                SaleJournalEntryId: editedAsset.saleJournalEntryId || null,
            };
            const response = await AssetService.UpdateAsset(updatedAsset, token);
            if (!response.success) {
                toast.error(t('Failed to update asset'));
                return;
            }

            if (!isChildAsset) {
                try {
                    const relationResp = await AssetService.SaveAssetRelation(
                        {
                            caseId,
                            rootAssetId: editedAsset.id,
                            relatedAssetIds: (selectedRelatedAssets || []).map((a) => a.id),
                        },
                        token
                    );
                    if (!relationResp?.success) {
                        toast.error(relationResp?.error || t('Failed to save asset relation'));
                        return;
                    }
                    if (typeof refreshRelations === 'function') {
                        try { await refreshRelations(); } catch (_) { /* non-fatal */ }
                    }
                } catch (relErr) {
                    const message = relErr?.message || t('Failed to save asset relation');
                    toast.error(message);
                    return;
                }
            }

            toast.success(t('Asset updated successfully'));
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            let message = t('An error occurred while updating asset');
            try {
                const parsed = JSON.parse(error?.message || '');
                if (parsed?.error) message = parsed.error;
            } catch (_) {
                if (error?.message && !error.message.startsWith('{')) {
                    message = error.message;
                }
            }
            toast.error(message);
        }
    };

    const handleLinkSaleTransaction = async () => {
        if (!saleTransactionId?.trim() || !editedAsset.sellDate) {
            toast.error(t('FillSellDateAndTransactionId') || 'יש למלא תאריך מכירה ומזהה תנועה');
            return;
        }
        try {
            setIsLinking(true);
            await AssetService.SetEndDate(
                caseId,
                editedAsset.id,
                saleTransactionType,
                saleTransactionId.trim(),
                editedAsset.sellDate,
                token,
                editedAsset.sellAmount ? parseFloat(editedAsset.sellAmount) : null
            );
            toast.success(t('SaleTransactionLinked') || 'תנועת מכירה שויכה בהצלחה');
            const updatedField = saleTransactionType === 'Incomes' ? 'saleIncomeId' : 'saleJournalEntryId';
            const clearedField = saleTransactionType === 'Incomes' ? 'saleJournalEntryId' : 'saleIncomeId';
            setEditedAsset(prev => ({ ...prev, [updatedField]: saleTransactionId.trim(), [clearedField]: null }));
            setSaleTransactionId('');
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToLinkSaleTransaction') || 'שיוך תנועת מכירה נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    const handleUnlinkSaleTransaction = async () => {
        try {
            setIsLinking(true);
            await AssetService.DetachDocumentFromAsset(
                caseId,
                editedAsset.id,
                editedAsset.saleIncomeId || editedAsset.saleJournalEntryId,
                token
            );
            toast.success(t('SaleTransactionUnlinked') || 'שיוך תנועת מכירה הוסר');
            setEditedAsset(prev => ({ ...prev, saleIncomeId: null, saleJournalEntryId: null }));
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToUnlinkSaleTransaction') || 'הסרת שיוך נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    const handleDetachPurchaseDocument = async () => {
        const transactionId = editedAsset.expenseId || editedAsset.journalEntryId;
        if (!transactionId) return;

        const result = await Swal.fire({
            title: t('ConfirmUnlinkPurchaseDocument'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#304FFF',
            cancelButtonColor: '#999',
            confirmButtonText: t('confirm') || t('Confirm') || 'אישור',
            cancelButtonText: t('Cancel') || 'ביטול',
        });

        if (!result.isConfirmed) return;

        try {
            setIsLinking(true);
            const response = await AssetService.DetachDocumentFromAsset(
                caseId,
                editedAsset.id,
                transactionId,
                token
            );
            if (!response?.success) {
                toast.error(t('FailedToUnlinkPurchaseDocument') || 'הסרת קישור מסמך הרכישה נכשלה');
                return;
            }
            toast.success(t('PurchaseDocumentUnlinkedSuccessfully') || 'קישור מסמך הרכישה הוסר בהצלחה');
            const updated = { ...editedAsset, expenseId: null, journalEntryId: null };
            setEditedAsset(updated);
            onSave(updated);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToUnlinkPurchaseDocument') || 'הסרת קישור מסמך הרכישה נכשלה');
        } finally {
            setIsLinking(false);
        }
    };

    const handlePickSaleTransaction = async (selected) => {
        const sellDateIso = editedAsset.sellDate
            || parseApiDateToUtcIso(selected.date)
            || new Date().toISOString();
        const sellAmount = Math.round(Number(selected.amount || 0) * 100) / 100;

        if (!editedAsset.sellDate) {
            handleChange('sellDate', sellDateIso);
        }
        handleChange('sellAmount', sellAmount);
        setSaleTransactionId(selected.id);
        setSaleTransactionType(selected.type === 'Incomes' ? 'Incomes' : 'JournalEntries');

        try {
            setIsLinking(true);
            await AssetService.SetEndDate(
                caseId,
                editedAsset.id,
                selected.type === 'Incomes' ? 'Incomes' : 'JournalEntries',
                selected.id,
                sellDateIso,
                token,
                sellAmount
            );
            toast.success(t('SaleTransactionLinked') || 'תנועת מכירה שויכה בהצלחה');
            const updatedField = selected.type === 'Incomes' ? 'saleIncomeId' : 'saleJournalEntryId';
            const clearedField = selected.type === 'Incomes' ? 'saleJournalEntryId' : 'saleIncomeId';
            setEditedAsset(prev => ({
                ...prev,
                [updatedField]: selected.id,
                [clearedField]: null,
                sellDate: sellDateIso,
                sellAmount,
            }));
            setSaleTransactionId('');
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToLinkSaleTransaction') || 'שיוך תנועת מכירה נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    if (!open) return null;

    const saleDisplaySource = isChildAsset && isGroupSoldViaRoot ? rootAssetForSale : editedAsset;
    const saleFieldsReadOnly = isChildAsset;
    const canRemoveSaleDate =
        !isChildAsset &&
        !!(
            editedAsset?.sellDate ||
            editedAsset?.saleIncomeId ||
            editedAsset?.saleJournalEntryId
        );
    const hasSaleDocument =
        saleDisplaySource?.saleJournalEntryId || saleDisplaySource?.saleIncomeId;
    const hasPurchaseDocument = Boolean(editedAsset.expenseId || editedAsset.journalEntryId);

    const dialog = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* ─── Header ─── */}
            <Box
                className="handle"
                sx={{
                    cursor: 'move',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2.5, py: 1.5,
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #304FFF 0%, #5c6fff 100%)',
                    borderRadius: '12px 12px 0 0',
                    color: '#fff',
                }}
                dir={direction}
            >
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{t('EditAsset')}</Typography>
                <Box sx={{ width: 40 }} />
                <Tooltip title={t('Exit')}>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                        {isDraggable ? <CancelIcon /> : <RemoveIcon />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ─── Body ─── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
                dir={direction}
            >

                {/* ══════ Section 1: Asset Information ══════ */}
                <SectionTitle>{t('AssetInformation') || 'פרטי נכס'}</SectionTitle>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
                    <Box>
                        <FieldLabel required>{t('AssetName')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('AssetName')}
                            value={editedAsset.name || ''}
                            onChange={(e) => handleChange('name', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </Box>
                    <Box>
                        <FieldLabel required>{t('OriginalPrice')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('OriginalPrice')}
                            type="number"
                            value={editedAsset.originalPrice ?? ''}
                            onChange={(e) => handleChange('originalPrice', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </Box>
                    <Box>
                        <FieldLabel required>{t('DepreciationPercentage')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('DepreciationPercentage')}
                            type="number"
                            value={editedAsset.depreciationPercentage ?? ''}
                            onChange={(e) => handleChange('depreciationPercentage', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </Box>
                    <Box>
                        <FieldLabel>{t('AssetGroup')}</FieldLabel>
                        <Autocomplete
                            size="small"
                            options={assetGroups}
                            getOptionLabel={(option) => `${option.assetGroupId} - ${option.name}`}
                            value={assetGroups.find((o) => o.assetGroupId === editedAsset.assetGroupId) || null}
                            onChange={(_, newValue) => handleChange('assetGroup', newValue || '')}
                            onInputChange={(_, v) => setSearchTerm(v)}
                            noOptionsText={<strong>{t('NoOptions')}</strong>}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('Select Asset Group')}
                                    variant="standard"
                                    InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <StyledListItem {...props}>
                                    {`${option.assetGroupId} - ${option.name}`}
                                </StyledListItem>
                            )}
                            PopperComponent={PopperWithDirection}
                        />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <FieldLabel>{t('Description')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('Description')}
                            value={editedAsset.description || ''}
                            onChange={(e) => handleChange('description', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2, borderColor: '#e0e4f5' }} />

                {/* ══════ Section 2: Dates ══════ */}
                <SectionTitle>{t('Dates') || 'תאריכים'}</SectionTitle>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
                    <Box>
                        <FieldLabel required>{t('PurchaseDate')}</FieldLabel>
                        <CustomDatePicker
                            date={editedAsset.purchaseDate || null}
                            onDateChange={(date) => handleChange('purchaseDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height="44px"
                            clearable
                        />
                    </Box>
                    <Box>
                        <FieldLabel required>{t('ActivationDate')}</FieldLabel>
                        <CustomDatePicker
                            date={editedAsset.activationDate || null}
                            onDateChange={(date) => handleChange('activationDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height="44px"
                            clearable
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2, borderColor: '#e0e4f5' }} />

                {/* ══════ Section 3: Sale Information ══════ */}
                <SectionTitle>{t('SaleInformation') || 'פרטי מכירה'}</SectionTitle>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <FieldLabel>{t('SellDate')}</FieldLabel>
                            {canRemoveSaleDate && (
                                <button
                                    onClick={handleRemoveSellDate}
                                    style={{
                                        fontSize: '11px', background: 'none', border: 'none',
                                        color: '#d32f2f', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                                    }}
                                >
                                    {t('RemoveDate')}
                                </button>
                            )}
                        </Box>
                        <CustomDatePicker
                            date={saleDisplaySource?.sellDate || null}
                            onDateChange={(date) => handleChange('sellDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height="44px"
                            clearable={!saleFieldsReadOnly}
                            disabled={saleFieldsReadOnly}
                        />
                    </Box>
                    <Box>
                        <FieldLabel>{t('SellAmount')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('SellAmount')}
                            type="number"
                            value={saleDisplaySource?.sellAmount ?? ''}
                            onChange={(e) => handleChange('sellAmount', e.target.value)}
                            variant="standard"
                            disabled={saleFieldsReadOnly || !saleDisplaySource?.sellDate}
                            InputProps={{
                                ...inputPropsStyle,
                                sx: {
                                    ...inputPropsStyle.sx,
                                    background: saleDisplaySource?.sellDate ? '#F5F5F6' : '#eee',
                                },
                            }}
                            inputProps={inputStyle}
                        />
                    </Box>
                </Box>

                {hasSaleDocument && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: '#fce4ec', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <LinkIcon sx={{ color: '#c62828', fontSize: 18 }} />
                        <Typography sx={{ fontSize: 12, color: '#c62828', fontWeight: 600 }}>
                            {t('LinkedSaleTransaction') || 'תנועת מכירה משויכת'}:
                        </Typography>
                        <Chip
                            size="small"
                            label={saleDisplaySource?.saleIncomeId ? `${t('Income') || 'הכנסה'} - ${saleDisplaySource.saleIncomeId.substring(0, 8)}...` : `${t('JournalEntry') || 'פקודת יומן'} - ${saleDisplaySource?.saleJournalEntryId?.substring(0, 8)}...`}
                            sx={{ fontSize: 11, backgroundColor: '#fff', border: '1px solid #ef9a9a' }}
                        />
                        {isDraggable && !isChildAsset && (
                            <>
                                <Button
                                    variant="outlined" size="small"
                                    startIcon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => viewDocument(editedAsset, 'sale')}
                                    sx={{ fontSize: 11, textTransform: 'none', borderColor: '#c62828', color: '#c62828', borderRadius: '6px', py: 0.3, '&:hover': { backgroundColor: 'rgba(198,40,40,0.06)' } }}
                                >
                                    {t('ViewSaleDocument') || 'צפה בתנועה'}
                                </Button>
                                <Button
                                    variant="text" size="small"
                                    startIcon={<LinkOffIcon sx={{ fontSize: 14 }} />}
                                    onClick={handleUnlinkSaleTransaction}
                                    disabled={isLinking}
                                    sx={{ fontSize: 11, textTransform: 'none', color: '#999', py: 0.3 }}
                                >
                                    {t('Unlink') || 'הסר שיוך'}
                                </Button>
                            </>
                        )}
                    </Box>
                )}

                {!hasSaleDocument && !isChildAsset && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: '#f5f5f5', border: '1px dashed #ccc' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#555', mb: 1 }}>
                            {t('LinkSaleTransaction') || 'שיוך תנועת מכירה'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap', mb: 1.5 }}>
                            <Button
                                variant="outlined" size="small"
                                startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setShowSalePicker(true)}
                                sx={{
                                    fontSize: 12, textTransform: 'none', borderRadius: '8px',
                                    borderColor: '#304FFF', color: '#304FFF', px: 2, py: 0.8,
                                    '&:hover': { borderColor: '#2040DD', backgroundColor: 'rgba(48,79,255,0.04)' },
                                }}
                            >
                                {t('BrowseTransactions') || 'חפש תנועות לפי מיון'}
                            </Button>
                        </Box>

                        {editedAsset.sellDate && (
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <Box sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 11, color: '#777', mb: 0.3 }}>{t('TransactionType') || 'סוג תנועה'}</Typography>
                                    <TextField
                                        select size="small" variant="standard"
                                        value={saleTransactionType}
                                        onChange={(e) => setSaleTransactionType(e.target.value)}
                                        InputProps={inputPropsStyle}
                                        SelectProps={{ native: true }}
                                        sx={{ '& select': { fontSize: 12, py: '8px' } }}
                                    >
                                        <option value="Incomes">{t('Income') || 'הכנסה'}</option>
                                        <option value="JournalEntries">{t('JournalEntry') || 'פקודת יומן'}</option>
                                    </TextField>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography sx={{ fontSize: 11, color: '#777', mb: 0.3 }}>{t('TransactionId') || 'מזהה תנועה (ID)'}</Typography>
                                    <TextField
                                        fullWidth size="small" variant="standard"
                                        placeholder={t('PasteTransactionId') || 'הדבק מזהה תנועה'}
                                        value={saleTransactionId}
                                        onChange={(e) => setSaleTransactionId(e.target.value)}
                                        InputProps={inputPropsStyle}
                                        inputProps={{ style: { fontSize: 12 } }}
                                    />
                                </Box>
                                <Button
                                    variant="contained" size="small"
                                    startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                    onClick={handleLinkSaleTransaction}
                                    disabled={isLinking || !saleTransactionId.trim()}
                                    sx={{
                                        fontSize: 11, textTransform: 'none', borderRadius: '6px',
                                        backgroundColor: '#c62828', py: 0.6, px: 2,
                                        '&:hover': { backgroundColor: '#b71c1c' },
                                        '&:disabled': { backgroundColor: '#eee' },
                                    }}
                                >
                                    {isLinking ? (t('Linking') || 'משייך...') : (t('LinkTransaction') || 'שייך תנועה')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                <Divider sx={{ my: 2, borderColor: '#e0e4f5' }} />

                {/* ══════ Section 4: Linking ══════ */}
                <SectionTitle>{t('Linking') || 'שיוך'}</SectionTitle>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
                    <Box>
                        <FieldLabel>{t('SerialNumber')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('SerialNumber')}
                            value={editedAsset.assetNumber || ''}
                            onChange={(e) => handleChange('assetNumber', e.target.value)}
                            variant="standard"
                            type="number"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </Box>
                    <Box>
                        <FieldLabel>{t('RelatedAssets') || 'נכסים מקושרים'}</FieldLabel>
                        {isChildAsset ? (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    backgroundColor: '#f0f3ff',
                                    border: '1px solid #c5ceff',
                                    borderRadius: '8px',
                                    color: '#304FFF',
                                    fontSize: 13,
                                }}
                            >
                                {`${t('LinkedTo') || 'מקושר ל'}-${parentRelation?.rootAsset?.name || ''}`}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                disableCloseOnSelect
                                options={eligibleRelatedAssets}
                                value={selectedRelatedAssets}
                                getOptionLabel={(option) => `${option.name} - ${option.assetNumber}`}
                                isOptionEqualToValue={(option, value) => sameAssetId(option?.id, value?.id)}
                                onChange={(_, newValue) => setSelectedRelatedAssets(newValue || [])}
                                noOptionsText={<strong>{t('NoOptions')}</strong>}
                                renderTags={(value, getTagProps) => {
                                    const visible = value.slice(0, 2);
                                    const hiddenCount = value.length - visible.length;
                                    return (
                                        <>
                                            {visible.map((option, index) => {
                                                const tagProps = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        {...tagProps}
                                                        key={option.id}
                                                        size="small"
                                                        label={`${option.name} - ${option.assetNumber || ''}`}
                                                        sx={{ maxWidth: 160 }}
                                                    />
                                                );
                                            })}
                                            {hiddenCount > 0 && (
                                                <Chip
                                                    size="small"
                                                    clickable
                                                    label={`+${hiddenCount}`}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedAssetsDialogOpen(true);
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#E8EBFF',
                                                        color: '#304FFF',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={selectedRelatedAssets.length === 0 ? (t('SelectRelatedAssets') || 'בחר נכסים מקושרים') : ''}
                                        variant="standard"
                                        InputProps={{
                                            ...params.InputProps,
                                            ...inputPropsStyle,
                                            sx: {
                                                ...inputPropsStyle?.sx,
                                                minHeight: 48,
                                                maxHeight: 48,
                                                overflow: 'hidden',
                                                flexWrap: 'nowrap',
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <StyledListItem {...props}>
                                        {`${option.name} - ${option.assetNumber}`}
                                    </StyledListItem>
                                )}
                                PopperComponent={PopperWithDirection}
                            />
                        )}
                    </Box>
                </Box>

                {/* Purchase document actions */}
                {isDraggable && hasPurchaseDocument && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                            onClick={() => viewDocument(editedAsset)}
                            sx={{
                                borderColor: '#304FFF', color: '#304FFF', textTransform: 'none',
                                borderRadius: '8px', fontSize: 12, px: 2, py: 0.8,
                                '&:hover': { borderColor: '#2040DD', backgroundColor: 'rgba(48,79,255,0.04)' },
                            }}
                        >
                            {t('ViewPurchaseDocument') || 'צפה בתנועת רכישה'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                            onClick={handleDetachPurchaseDocument}
                            disabled={isLinking}
                            sx={{
                                borderColor: '#d32f2f', color: '#d32f2f', textTransform: 'none',
                                borderRadius: '8px', fontSize: 12, px: 2, py: 0.8,
                                '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211,47,47,0.04)' },
                            }}
                        >
                            {t('UnlinkPurchaseDocument') || 'נתק מסמך רכישה'}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* ─── Footer Actions ─── */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1.5,
                    flexShrink: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    px: 3,
                    py: 2,
                    mt: 'auto',
                    borderTop: '1px solid #e0e4f5',
                    backgroundColor: '#fafbff',
                    borderRadius: '0 0 12px 12px',
                }}
                dir={direction}
            >

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                    onClick={handleDeleteClick}
                    sx={{
                        borderColor: '#d32f2f',
                        color: '#d32f2f',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: 13,
                        px: 3,
                        py: 0.8,
                        m: 0,
                        '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211,47,47,0.04)' },
                    }}
                >
                    {t('Delete')}
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    sx={{
                        backgroundColor: '#304FFF',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: 13,
                        px: 3,
                        py: 0.8,
                        m: 0,
                        '&:hover': { backgroundColor: '#2040DD' },
                        '&:disabled': { backgroundColor: '#c5ceff', color: '#fff' },
                    }}
                >
                    {t('Save')}
                </Button>
            </Box>
        </Box>
    );

    return (
        <>
            {isDraggable ? (
                <Draggable handle=".handle" nodeRef={nodeRef}>
                    <StyledContainer ref={nodeRef}>
                        {dialog}
                    </StyledContainer>
                </Draggable>
            ) : (
                dialog
            )}
            <SaleTransactionPicker
                open={showSalePicker}
                onClose={() => setShowSalePicker(false)}
                onSelect={handlePickSaleTransaction}
                caseId={caseId}
                accountantId={accountantId}
                token={token}
                sortCodeOptions={sortCodeIdOptions}
            />

            <Dialog
                open={selectedAssetsDialogOpen}
                onClose={() => setSelectedAssetsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                // StyledContainer (EditAsset) has zIndex: 1500 — this Dialog must sit above it.
                sx={{ zIndex: 100000 }}
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        overflow: 'hidden',
                        border: '1px solid #c5ceff',
                        boxShadow: '0 16px 48px rgba(48, 79, 255, 0.22), 0 4px 12px rgba(0,0,0,0.08)',
                    },
                    dir: direction,
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 2.5,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #304FFF 0%, #5c6fff 100%)',
                        color: '#fff',
                    }}
                    dir={direction}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {t('SelectedAssets') || 'הנכסים שנבחרו'}
                        </Typography>
                        <Box
                            sx={{
                                ml: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: '999px',
                                backgroundColor: 'rgba(255,255,255,0.22)',
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.4,
                            }}
                        >
                            {selectedRelatedAssets.length}
                        </Box>
                    </Box>
                    <Tooltip title={t('Close') || 'סגור'}>
                        <IconButton
                            size="small"
                            onClick={() => setSelectedAssetsDialogOpen(false)}
                            aria-label="close"
                            sx={{
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 0,
                        backgroundColor: '#fafbff',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                    }}
                    dir={direction}
                >
                    {selectedRelatedAssets.length === 0 ? (
                        <Box
                            sx={{
                                py: 5,
                                px: 3,
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontWeight: 500,
                            }}
                        >
                            {t('NoOptions')}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 2 }}>
                            {[...selectedRelatedAssets]
                                .sort((a, b) => {
                                    const an = Number(a?.assetNumber);
                                    const bn = Number(b?.assetNumber);
                                    const aNum = Number.isFinite(an);
                                    const bNum = Number.isFinite(bn);
                                    if (aNum && bNum) return an - bn;
                                    if (aNum) return -1;
                                    if (bNum) return 1;
                                    return String(a?.assetNumber ?? '').localeCompare(String(b?.assetNumber ?? ''));
                                })
                                .map((option) => (
                                <Box
                                    key={option.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e4f5',
                                        borderRadius: '10px',
                                        px: 1.5,
                                        py: 1,
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            borderColor: '#304FFF',
                                            boxShadow: '0 2px 8px rgba(48,79,255,0.10)',
                                        },
                                    }}
                                    dir={direction}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '6px',
                                                backgroundColor: '#E8EBFF',
                                                color: '#304FFF',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1.4,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {option.assetNumber || '—'} -
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: '#1f2447',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {option.name}
                                        </Typography>

                                    </Box>
                                    <Tooltip title={t('Remove') || 'הסר'}>
                                        <IconButton
                                            size="small"
                                            aria-label="remove"
                                            onClick={() =>
                                                setSelectedRelatedAssets((prev) =>
                                                    prev.filter((a) => a.id !== option.id)
                                                )
                                            }
                                            sx={{
                                                color: '#9099b7',
                                                flexShrink: 0,
                                                '&:hover': {
                                                    color: '#d32f2f',
                                                    backgroundColor: 'rgba(211,47,47,0.08)',
                                                },
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderTop: '1px solid #e0e4f5',
                        backgroundColor: '#fff',
                    }}
                    dir={direction}
                >
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setSelectedAssetsDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 2.5,
                            py: 0.8,
                            backgroundColor: '#304FFF',
                            boxShadow: '0 2px 6px rgba(48,79,255,0.25)',
                            '&:hover': {
                                backgroundColor: '#2040DD',
                                boxShadow: '0 4px 10px rgba(48,79,255,0.35)',
                            },
                        }}
                    >
                        {t('Close') || 'סגור'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditAsset;
